// 작업 공간 배치 — 왼쪽 채널, 오른쪽 타임라인과 입력칸.
//
// 이 파일은 **아무것도 가져오지 않는다.** 목록도 줄도 보내기 함수도 props 다.
// 그래서 웹·데스크톱·모바일이 같은 화면을 쓰고, 시험이 브라우저 없이 돈다.

import type { ChannelId, MarkupNames, Seq, UserId } from '@maru/protocol'
import {
  ArrowLeftRight,
  Bell,
  Bookmark as BookmarkIcon,
  CalendarClock,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Compass,
  Ellipsis,
  Files,
  FileText,
  Hash,
  Inbox,
  ListFilter,
  Lock,
  MessagesSquare,
  NotebookPen,
  Plus,
  Search,
  SlidersHorizontal,
  SquarePen,
  Star,
  UserPlus,
  UsersRound,
} from 'lucide-react'
import { lazy, type ReactNode, Suspense, useEffect, useEffectEvent, useRef, useState } from 'react'
import type { Act } from './activity_panel.tsx'
import type { AiDigest } from './ai_digest.tsx'
import type { FileMap } from './attachment_view.tsx'
import type { EmojiLook, GroupLook } from './body_view.tsx'
import type { Bookmark } from './bookmark_bar.tsx'
import type { CanvasHands } from './canvas_panel.tsx'
import type { DetailsGo, DetailsTab } from './channel_details.tsx'
import type { ChannelEdit, Findable, PeekLine, PrefEdit } from './channel_panels.tsx'
import { CHANNEL_DRAG, ChannelRow, DmFace, SECTION_DRAG } from './channel_row.tsx'
import type { StartClip, Transcribe } from './clip.ts'
import { type Attached, type CommandReply, Composer } from './composer.tsx'
import { ConfirmDialog } from './confirm.tsx'
import type { ConnectLinkView } from './connect_box.tsx'
import type { DmRow } from './dms_panel.tsx'
import { snoozed } from './format.ts'
import { HuddleButton, type HuddleHands } from './huddle_bar.tsx'
import { msg, useT } from './i18n.ts'
import { History, type Nav, nav_of_key, step_channel } from './keymap.ts'
import { Button, IconButton, LEAD_SIZE } from './kit/button.tsx'
import { Avatar, Badge } from './kit/display.tsx'
import { EmptyState, SkeletonText } from './kit/feedback.tsx'
import { Tooltip } from './kit/tooltip.tsx'
import type { LaterPatch } from './later_panel.tsx'
import type { CardMap } from './link_card.tsx'
import type { ListHands } from './list_panel.tsx'
import type { NewKind } from './manage_panels.tsx'
import type { Mark } from './mark_panel.tsx'
import type { Candidate } from './mention_menu.tsx'
import { MessageList } from './message_list.tsx'
import type { Line, RowActions } from './message_row.tsx'
import { humans_in, PeoplePanel, type Person } from './people_panel.tsx'
import { PlaceKinds, place_kind } from './place_mark.tsx'
import type { PollLook } from './poll_card.tsx'
import type { PollDraftLook } from './poll_form.tsx'
import type { ProfileView } from './profile_card.tsx'
import type { ActionHands, SwitchItem } from './quick_switch.tsx'
import type { Tally } from './reaction_bar.tsx'
import type { RoomMenuItem } from './row_menu.tsx'
import type { Booking, BookingChange } from './schedule_panel.tsx'
import type { FileHit, Hit } from './search_panel.tsx'
import type { SettingsTab } from './settings_page.tsx'
import type { ShortcutItem } from './shortcut_menu.tsx'
import {
  arrange_rows,
  badge_of,
  group_channels,
  move_section,
  type Notify,
  type SectionSpec,
  SIDEBAR_VIEW_DEFAULT,
  type Section as SidebarPart,
  type SidebarView,
  visible_rows,
} from './sidebar.ts'
import type { StatusEdit, StatusValue } from './status_dialog.tsx'
import type { SummaryHow } from './summary_panel.tsx'
import type { Brief } from './thread_list.tsx'
import { TypingLine } from './typing_line.tsx'
import type { UnreadGroup } from './unread_panel.tsx'
import { type WhisperItem, Whispers } from './whispers.tsx'

// 첫 화면에 없는 옆 판은 열 때 받는다(AGENTS.md 「렌더링·스타일 규약」). 이 판들이 첫 청크에
// 있으면 로그인만 한 사람도 검색·리스트·요약 코드를 받는다. 자리는 `Suspense` 가 잠깐 비운다.
// ⌘K·⌘/ 창은 누를 때만 받는다 — 창 틀·키 표시·목록 그림이 첫 화면에 실릴 까닭이 없다.
// 관리 판의 틀은 옆 판(전달)을 열 때 받는다 — 나머지 관리 판은 이미 lazy 인 `manage_panels` 가 쓴다.
const ManageSide = lazy(() => import('./manage_side.tsx').then((m) => ({ default: m.ManageSide })))
const QuickSwitch = lazy(() =>
  import('./quick_switch.tsx').then((m) => ({ default: m.QuickSwitch })),
)
const ShortcutSheet = lazy(() =>
  import('./quick_switch.tsx').then((m) => ({ default: m.ShortcutSheet })),
)
// 북마크 줄은 채널을 연 뒤 목록을 받아야 서므로, 받는 동안 코드도 함께 받는다(docs/design2.md E18).
const BookmarkBar = lazy(() =>
  import('./bookmark_bar.tsx').then((m) => ({ default: m.BookmarkBar })),
)
const HuddleDock = lazy(() => import('./huddle_dock.tsx').then((m) => ({ default: m.HuddleDock })))
// 캔버스 판은 누를 때만 선다(D15) — 판이 끌고 오는 Textarea·아이콘을 첫 화면에 싣지 않는다.
// 활동·핀·안 읽음·예약도 사이드바를 눌러야 서는 판이다(D16).
// 스레드 창·모아 보기는 누를 때만 선다(D12). 둘이 끌고 오는 아이콘·Checkbox 가 첫 화면 +2 kB 였다.
const ThreadPanel = lazy(() =>
  import('./thread_panel.tsx').then((m) => ({ default: m.ThreadPanel })),
)
// 채널 관리 판(새 채널·사람 고르기·찾아보기·고치기·내 알림·채널 관리자)도 누를 때만 선다(D18).
// 사람 쪽지창은 이름을 누를 때만 선다(D17).
const RowMenuAt = lazy(() => import('./row_menu.tsx').then((m) => ({ default: m.RowMenuAt })))
const MeMenuAt = lazy(() => import('./row_menu.tsx').then((m) => ({ default: m.MeMenuAt })))
const StatusDialog = lazy(() =>
  import('./status_dialog.tsx').then((m) => ({ default: m.StatusDialog })),
)
const GettingStarted = lazy(() =>
  import('./getting_started.tsx').then((m) => ({ default: m.GettingStarted })),
)
const OrgMenuAt = lazy(() => import('./row_menu.tsx').then((m) => ({ default: m.OrgMenuAt })))
const RoomMenuAt = lazy(() => import('./row_menu.tsx').then((m) => ({ default: m.RoomMenuAt })))
// 채널 세부정보 창(G21) — 이름을 누를 때 받는다. 옆 판 청크(`manage_side`)에 얹는다 — 제 청크를 따로 두면
// 첫 화면과 나눠 쓰는 모듈(프로토콜·입력칸·창)이 쪼개져 나가 첫 화면이 +1.9 kB 커졌다.
const ChannelDetails = lazy(() =>
  import('./manage_side.tsx').then((m) => ({ default: m.ChannelDetailsSide })),
)
// 쪽지창이 사람을 묻는 손까지 그 청크에 산다 — 첫 화면에 싣지 않는다.
const WhoSide = lazy(() => import('./profile_card.tsx').then((m) => ({ default: m.WhoSide })))

/** 채널을 다루는 손. 없으면 단추도 없다(읽기 전용 화면). */
export interface Manage {
  create_channel(kind: NewKind, name: string): Promise<void>
  open_dm(with_: readonly UserId[]): Promise<void>
  /** 지금 연 채널에 들인다. */
  invite(who: readonly UserId[]): Promise<void>
  /** 지금 연 채널에서 나간다. */
  leave(): Promise<void>
  /** 지금 연 채널에서 남을 내보낸다. 대화방·게스트에게는 없다. */
  kick?: import('./channel_details.tsx').Kick | undefined
  /** 지금 연 대화방을 사이드바에서 닫는다. 대화방일 때만 준다 — 없으면 단추도 없다. */
  close_dm?(): Promise<void>
  search(text: string, before?: string): Promise<Hit[]>
  /** 파일 탭. 없으면 탭도 없다. */
  search_files?(text: string, before?: string): Promise<FileHit[]>
  /** 검색 결과로 짓는 AI 답. 없으면(서버에서 꺼져 있으면) 단추도 없다. */
  answer?(question: string): Promise<AiDigest | null>
  /** 허들. 없으면(서버에서 꺼져 있으면) 🎧 단추도 도크도 없다. */
  huddle?: HuddleHands
  /** 채널 AI 요약. 없으면 머리글의 요약 단추도 없다. */
  summarize?(channel: ChannelId, how: SummaryHow): Promise<AiDigest | null>
  /** 지금 연 채널의 이름·주제·보관을 고친다. 없으면 고치기 단추도 없다. */
  edit_channel?(patch: ChannelEdit): Promise<void>
  /** 지금 연 채널을 영구히 지운다. 없으면 삭제 칸도 없다. */
  remove_channel?(): Promise<void>
  /** 지금 연 그룹 대화를 비공개 채널로 바꾼다. */
  convert_dm?(name: string): Promise<void>
  /** 사이드바 칸 목록 전체를 저장한다(순서·그림·접힘). 없으면 칸 관리도 접기도 없다. */
  save_sections?(list: readonly SectionSpec[]): Promise<void>
  /** 칸 이름을 바꾼다. 그 칸의 채널이 따라간다. */
  rename_section?(from: string, to: string): Promise<void>
  /** 채널을 칸으로 옮긴다. `null` 은 기본 칸. */
  move_to_section?(channel: ChannelId, section: string | null): Promise<void>
  /** 채널 관리자 명단. 없으면 고치기 판에 명단이 없다. */
  managers?(channel: ChannelId): Promise<readonly UserId[]>
  /**
   * 지금 연 채널을 내가 돌보는가(조직 관리자이거나 채널 관리자). 거짓이면 편집 판은 읽기만 되고
   * 캔버스 지우기는 만든 사람에게만 선다. 모르는 동안은 `undefined` — 손잡이를 잠가 둔다.
   */
  manager?: boolean | undefined
  /** 조직 관리자인가. 남이 만든 리스트를 지우는 단추가 여기에 달린다. */
  admin?: boolean
  /** 채널 관리자를 정하거나 푼다. 자격은 서버가 본다. */
  set_manager?(channel: ChannelId, who: UserId, on: boolean): Promise<void>
  /** 바깥 조직과의 이음(Slack Connect). 없으면 고치기 판에 연결 칸이 없다. */
  connect?: ConnectHands
  /** 지금 연 채널의 내 알림·즐겨찾기·칸을 맞춘다. */
  set_pref?(patch: PrefEdit): Promise<void>
  /** 사이드바 줄 메뉴와 머리의 별표 — 연 채널이 아니어도 그 줄의 채널에 한다. 없으면 메뉴도 없다. */
  row?: RowHands
  /** 들어갈 수 있는 공개 채널. 없으면 찾아보기 단추도 없다. */
  browse?(): Promise<readonly Findable[]>
  /** 찾아보기 목록의 세대. 바뀌면 열린 판이 목록을 다시 받는다(비공개 전환). */
  browse_epoch?: number
  /** 찾아보기에서 고른 채널에 든다. */
  join?(id: ChannelId): Promise<void>
  /** 들기 전에 그 채널의 최근 글을 받는다(G35). 없으면 찾아보기의 이름은 글자로만 선다. */
  peek?(id: ChannelId): Promise<readonly PeekLine[]>
  /** 보관한 채널. 없으면 찾아보기에 "보관된 채널만" 칸이 없다. */
  archived?(): Promise<readonly Findable[]>
  /** 보관을 푼다. */
  unarchive?(id: ChannelId): Promise<void>
  /** 채널의 리스트. 없으면 리스트 단추도 없다. */
  lists?: ListHands
  /** 채널 캔버스(공동 편집 문서). 없으면 캔버스 단추도 없다. */
  canvas?: CanvasHands
  /** 채널 머리의 북마크. 없으면 북마크 줄도 없다. */
  bookmarks?(channel: ChannelId): Promise<readonly Bookmark[]>
  /** 북마크를 건다. 제목이 비면 서버가 주소를 제목으로 쓴다. */
  bookmark_add?(channel: ChannelId, title: string, url: string): Promise<void>
  /** 북마크를 뗀다. 구성원 누구든 뗀다. */
  bookmark_remove?(channel: ChannelId, id: number): Promise<void>
  /** 북마크의 제목·주소를 고친다. 구성원 누구든 고친다. */
  bookmark_edit?(channel: ChannelId, id: number, title: string, url: string): Promise<void>
  /** 지금 연 채널의 핀. 없으면 핀 판도 없다. */
  pins?(channel: ChannelId): Promise<readonly Mark[]>
  /** 내가 담아 둔 줄들. */
  saved_marks?(): Promise<readonly Mark[]>
  /** 핀을 꽂거나 뺀다. 판에서 빼는 데 쓴다. */
  on_pin?(channel: ChannelId, seq: Seq, on: boolean): Promise<void>
  /** 나중에 보기에 담거나 뺀다. */
  on_save?(channel: ChannelId, seq: Seq, on: boolean): Promise<void>
  /** 담아 둔 줄의 탭(진행 중·보관·완료)을 옮기거나 기한을 건다. */
  on_later?(channel: ChannelId, seq: Seq, patch: LaterPatch): Promise<void>
  /** 이 줄을 저 채널로 전달한다. 없으면 전달 단추도 없다. */
  forward?(from: ChannelId, seq: Seq, to: ChannelId): Promise<void>
  /** 내가 낀 스레드들. 없으면 스레드 단추도 없다. */
  my_threads?(): Promise<readonly Brief[]>
  /** 나를 향한 일들. 없으면 활동 단추도 없다. */
  activity?(): Promise<readonly Act[]>
  /** 든 채널을 모두 읽음으로. */
  read_all?(): Promise<void>
  /** 안 읽은 줄을 대화별로. 없으면 안 읽음 단추도 없다. */
  unreads?(): Promise<readonly UnreadGroup[]>
  /** 한 대화를 이 줄까지 읽음으로. */
  read_upto?(channel: ChannelId, upto: Seq): Promise<void>
  /** 걸어 둔 예약들. 없으면 예약 단추도 없다. */
  bookings?(): Promise<readonly Booking[]>
  /**
   * 예약이 바뀌었을 수 있다는 신호(오를 때마다 판이 다시 받는다). 걸거나 거두거나, 내 이름으로
   * 새 줄이 서서(예약이 나갔다) 오른다 — 없으면 나간 예약이 판에 남아 있었다(3차 F154).
   */
  bookings_rev?: number
  /** 북마크 판. 다른 화면이 북마크를 바꿨다는 사건이 오면 오른다 — 줄을 다시 받는다. */
  bookmarks_rev?: number
  /** 예약을 거둔다. */
  unbook?(id: number): Promise<void>
  /** 예약을 기다리지 않고 지금 보낸다(G22). */
  book_now?(id: number): Promise<void>
  /** 예약의 본문·때를 고친다(G22). */
  rebook?(id: number, change: BookingChange): Promise<void>
  /** 예약 고치기 칸이 호출을 펼 이름들. */
  booking_names?: MarkupNames
}

/** 오른쪽 자리에 무엇을 그리는가. 스레드(props)는 이것들보다 우선한다. */
/** 줄로 뛰어도 열어 두는 판 — 결과 목록이라 다음 것을 누를 수 있어야 한다. */
const STAYS: ReadonlySet<string> = new Set([
  'search',
  'activity',
  'saved',
  'threads',
  'unreads',
  'scheduled',
  'pins',
  'files',
  'channel_files',
  'drafts',
  'dms',
])

const BOUND: ReadonlySet<string> = new Set(['invite', 'edit', 'pref', 'forward'])

/** 만들고 고치는 판 — 옆 자리가 아니라 화면 가운데 창으로 뜬다(슬랙). 옆 판(스레드·검색…)은 그 뒤에 그대로 있다. */
const MODAL: ReadonlySet<string> = new Set(['new', 'dm', 'invite', 'edit', 'pref', 'forward'])

/** 늦게 서는 칸에 초점을 둔다 — 옆 판은 늦은 청크라 누른 순간엔 칸이 없다. 2초 안에 안 서면 만다. */
function focus_later(selector: string) {
  const find = () => document.querySelector<HTMLElement>(selector)
  const now = find()
  if (now !== null) {
    now.focus()
    return
  }
  const watch = new MutationObserver(() => {
    const el = find()
    if (el === null) return
    watch.disconnect()
    el.focus()
  })
  watch.observe(document.body, { childList: true, subtree: true })
  setTimeout(() => watch.disconnect(), 2000)
}

/** 옆 판이 화면을 덮는 폭인가(`style.css` 의 720px 가름과 같다). */
function narrow(): boolean {
  return globalThis.matchMedia?.('(max-width: 720px)').matches === true
}

export type Panel =
  | 'people'
  | 'search'
  | 'new'
  | 'dm'
  | 'invite'
  | 'who'
  | 'browse'
  | 'edit'
  | 'pref'
  | 'pins'
  | 'lists'
  | 'canvas'
  | 'saved'
  | 'activity'
  | 'threads'
  | 'unreads'
  | 'forward'
  | 'scheduled'
  | 'sections'
  | 'summary'
  /** 내가 올린 파일(검색 판의 파일 탭, G29). */
  | 'files'
  /** 이 채널의 파일. */
  | 'channel_files'
  /** 쓰다 만 글(G28). */
  | 'drafts'
  /** 닫은 것까지 모든 대화방(G33). */
  | 'dms'

/** 줄 하나에 하는 일. 결과는 부르는 쪽이 토스트로 알린다 — 나가기만 실패를 던져 확인 창이 까닭을 적는다. */
export interface RowHands {
  pref(channel: ChannelId, patch: PrefEdit): Promise<void>
  /** 그 채널의 끝까지 읽음으로. */
  read(channel: ChannelId): Promise<void>
  leave(channel: ChannelId): Promise<void>
  /** 대화방을 사이드바에서 닫는다. */
  close_dm(channel: ChannelId): Promise<void>
}

/** 채널을 바깥 조직과 잇는 손. 초대·끊기는 관리자에게만 준다. */
export interface ConnectHands {
  of(channel: ChannelId): Promise<readonly ConnectLinkView[]>
  invite?(channel: ChannelId): Promise<string>
  unlink?(channel: ChannelId, peer: number): Promise<void>
}

/** 나 혼자의 방(메모장 — 리마인더가 오는 곳). 명단을 모르면(옛 서버) 아니라고 본다. */
function is_note(here: ChannelPick): boolean {
  return here.dm === true && here.dm_with !== undefined && here.dm_with.length === 0
}

export interface ChannelPick {
  id: ChannelId
  name: string
  /** 이어진 바깥 조직 이름들(Slack Connect). 비었거나 없으면 표시도 없다. */
  external?: readonly string[]
  /** 머리글에 한 줄로 걸린다. 없으면 자리도 없다. */
  topic?: string | null
  stored?: boolean
  /** 공지 채널. */
  announce?: boolean
  /** 비공개인가. **바꿀 수 있을 때만**(관리자·공개/비공개 채널) 채운다 — 없으면 전환 칸도 없다. */
  private?: boolean
  /**
   * 비공개 채널인가 — 보이기용이라 누구에게나 채운다(`#` 대신 자물쇠). 위 `private` 는 전환 손잡이라 관리자에게만
   * 있어서, 그것으로 그리면 구성원은 비공개 채널을 `#` 로 봤다(3차 F138).
   */
  secret?: boolean
  /** 기본 채널인가. 관리자·공개 채널에만 채운다 — 없으면 칸도 없다. */
  default_join?: boolean
  /** 나는 여기에 새 글을 못 쓴다(공지 채널인데 관리자가 아니다). 입력칸 대신 안내가 뜬다. */
  locked?: boolean
  /** DM·그룹 DM 인가. 줄 메뉴가 나가기 대신 닫기를 준다. */
  dm?: boolean
  /** 대화방의 나 아닌 사람들 — 얼굴(`DmFace`)을 고른다. */
  dm_with?: readonly UserId[]
  /** 나 혼자의 방이면 나 — 방 이름 글자 대신 내 얼굴을 그린다. */
  dm_self?: { user: UserId; name: string }
  /** 내 취향. 없으면 기본값이다. */
  notify?: Notify
  favorite?: boolean
  section?: string | null
  unread: number
  mention_count: number
}

/** 새 워크스페이스의 첫 할 일(3차 F32). 단계의 체크는 자료가 한다. */
export interface Starter {
  /** `go` 가 없으면 새 채널 판을 연다. */
  steps: readonly {
    key: 'invite' | 'channel' | 'profile'
    label: string
    done: boolean
    go?: (() => void) | undefined
  }[]
  dismiss(): void
}

/** 사이드바 맨 위의 워크스페이스 이름과 그 메뉴(3차 F31). */
export interface OrgHands {
  name: string
  /** 전환할 수 있는 다른 조직들. */
  others: readonly { tenant: number; name: string }[]
  switch(tenant: number): Promise<void>
  /** 워크스페이스를 하나 더 만든다(가입 화면으로). */
  create?(): void
  settings?(): void
  /** 사람 초대 — 관리자에게만. */
  invite?(): void
}

/** 아직 안 본 활동. 사이드바 '활동' 줄의 배지와 판 새로 받기가 이것 하나로 움직인다(3차 F58). */
export interface ActivityNews {
  /** 마지막으로 판을 연 뒤 새로 온 활동 수. */
  count: number
  /** 가장 새 활동의 때. 바뀌면 열려 있는 판이 다시 받는다. */
  stamp: string
  /** 판을 열어 봤다. */
  saw(): void
}

export interface WorkspaceProps extends RowActions {
  /** 채널 목록을 아직 받는 중이다. 빈 목록을 "참여한 채널이 없다" 로 읽지 않는다. */
  channels_loading?: boolean
  channels: ChannelPick[]
  /**
   * 사이드바에 없는 방(닫은 대화방·나와의 대화)의 이름. 예약·나중에·활동 판은 그런 방도 가리키는데,
   * `channels` 만 보면 `#9`·"알 수 없는 채널" 로 섰다(3차 F149).
   */
  room_name?: ((id: ChannelId) => string | undefined) | undefined
  activity_news?: ActivityNews
  org?: OrgHands
  starter?: Starter
  /** 아무 채널도 없거나 아직 못 고른 상태. */
  open: ChannelId | null
  pick(id: ChannelId): void
  lines: readonly Line[]
  read_upto: Seq
  loading?: boolean
  reached_start?: boolean
  on_reach_top?(): void
  name_of(user: UserId): string | undefined
  /** 봇인가 — 줄 머리의 "봇" 표식(3차 F85). */
  bot_of?: ((user: UserId) => boolean) | undefined
  /** 그룹 번호 → 이름과 "내가 그 안에 있는가". 없으면 번호로 그린다. */
  group_of?: GroupLook | undefined
  /** 커스텀 이모지 이름 → 그림 주소. */
  emoji_of?: EmojiLook | undefined
  me: UserId
  send(written: string, attachments: readonly Attached[]): Promise<void>
  attach?(file: File): Promise<Attached>
  /** 클립 녹음기. 기기에 녹음기가 없으면 비운다. */
  record?: StartClip | undefined
  /** 음성 클립 받아쓰기(`Composer`). */
  transcribe?: Transcribe | undefined
  typing?(): void
  /** 지금 이 채널에 쓰는 중인 사람들의 이름. 나는 빼고 준다. */
  typing_names?: readonly string[]
  /** 이 채널에서 앱이 나에게만 보인 글. 저장되지 않아 새로 고치면 사라진다. */
  whispers?: readonly WhisperItem[]
  dismiss_whisper?(key: number): void
  /** 이 채널의 사람들. 없으면 옆 판을 그리지 않는다. */
  people?: readonly Person[]
  /** 내 사이드바 칸들, 순서대로. 정한 칸은 비어 있어도 머리글이 선다. */
  sections?: readonly SectionSpec[]
  /** 닫은 것까지 모든 대화방(G33). 없으면 "모든 대화" 줄도 없다. */
  all_dms?: readonly DmRow[]
  /** 사이드바 보기(정렬·안 읽은 것만). 이 기기의 것이다(G25). */
  sidebar_view?: SidebarView
  /** 보기를 바꾼다. 없으면 머리의 "안 읽은 것만" 단추도 없다. */
  set_sidebar_view?(v: SidebarView): void
  /**
   * `@` 자동완성에 사람과 함께 뜨는 그룹들. **사람 판에는 안 낀다** — 그룹은
   * 이름이지 사람이 아니고, 옆 판은 "지금 여기 누가 있나" 를 답하는 자리다.
   */
  groups?: readonly Candidate[]
  tallies?: ReadonlyMap<Seq, readonly Tally[]>
  /** seq → 투표(`MessageList.polls`). */
  polls?: ReadonlyMap<Seq, PollLook | null>
  reply_counts?: ReadonlyMap<Seq, number>
  /** seq → 마지막 답글 시각(`MessageList.reply_last`). */
  reply_last?: ReadonlyMap<Seq, string>
  /** 열린 스레드. 있으면 사람 판 대신 스레드 창을 그린다. */
  thread?: ThreadView
  files?: FileMap
  cards?: CardMap
  manage?: Manage
  /** 조직의 사람들(나는 빼고). 대화 열기·초대에서 고른다. */
  directory?: readonly Person[]
  /** 설정 화면을 연다(탭을 주면 그 탭으로). 없으면 단추도 없다. */
  /** 환경설정을 연다. `aim` 은 그 탭에서 곧장 내려갈 섹션 제목(원문)이다. */
  settings?(tab?: SettingsTab, aim?: string): void
  /** 사이드바 아래 내 자리 — 설정·로그아웃이 그 메뉴에 있다. 없으면 자리도 없다(⌘, 는 그대로). */
  self?: SelfView
  /** 이 기기에서 로그아웃한다. 없으면 내 자리 메뉴에 로그아웃이 없다. */
  sign_out?(): Promise<void>
  /** 알림 일시 중지를 건다(`null` 은 푼다). 있으면 내 자리 메뉴에서 바로 고른다(F12). */
  snooze?(until: string | null): Promise<void>
  /** 자리 비움을 켜고 끈다. 있으면 내 자리 메뉴에 항목이 선다(3차 F41). */
  set_away?(on: boolean): Promise<void>
  /** 상태를 정한다(`null` 은 지우기). 있으면 내 메뉴의 [상태 설정] 이 설정 대신 창을 연다. */
  set_status?(status: StatusEdit): Promise<void>
  /** `/` 자동완성에 보일 커맨드들. */
  commands?: readonly Candidate[]
  /** 커맨드를 부른다. 답은 부른 사람에게만 보인다. */
  run?(name: string, text: string): Promise<CommandReply>
  /** 커맨드 명단을 처음 쓸 때 받는다(`Composer.load_commands`). */
  load_commands?(): Promise<readonly Candidate[]>
  /** 작성칸 곁의 전역 단축들(슬랙 global shortcuts). `on_global` 과 함께. */
  globals?: readonly ShortcutItem[]
  /** 이 채널에서 전역 단축을 부른다. */
  on_global?(channel: ChannelId, key: string): Promise<void>
  /**
   * 검색 결과의 그 줄로 뛴다. 과거를 당겨야 할 수도 있어 시간이 걸린다.
   * 못 닿으면 `false` — 채널만 열린 채로 둔다.
   */
  jump?(channel: ChannelId, seq: Seq): Promise<boolean>
  /**
   * 바깥(영구 링크·알림)이 가리킨 줄. 바뀔 때마다 검색에서 뛸 때와 같은 길로 그 줄까지 옮기고 강조한다(3차 F165).
   * 같은 줄을 다시 가리키려면 새 객체를 준다.
   */
  aim?: { channel: ChannelId; seq: Seq } | null
  /** 한 사람을 자세히. 없으면 이름은 그냥 글자다. */
  profile_of?(who: UserId): Promise<ProfileView>
  /** 지금 채널에서 핀이 꽂힌 줄들. */
  pinned?: ReadonlySet<Seq>
  /** 내 키워드. 대화 속 남의 글에서 칠한다(3차 F169). */
  words?: readonly string[] | undefined
  /** 지금 채널에서 내가 담아 둔 줄들. */
  saved?: ReadonlySet<Seq>
  /** 나중에 보낸다. 없으면 예약 단추도 없다. */
  schedule?(written: string, attachments: readonly Attached[], due_at: string): Promise<void>
  /** 투표를 올린다(`Composer.poll`). */
  poll?(draft: PollDraftLook): Promise<void>
  /** 쓰다 만 글이 있는 채널들. 사이드바에 연필이 붙는다. */
  drafts?: ReadonlySet<ChannelId>
  /** 이 채널에 쓰다 만 글. 입력칸의 처음 값이다. */
  draft_of?(channel: ChannelId): string
  /** 쓰다 만 글이 바뀌었다. 저장은 부르는 쪽이 한다. */
  on_draft?(channel: ChannelId, text: string): void
}

/** 사이드바 아래에 보이는 나. */
export interface SelfView {
  /** 내 번호. 주면 사이드바 얼굴에 내 사진이 선다. */
  id?: number
  name: string
  /** 이모지와 글을 한 줄로. 없으면 이름만. */
  status: string | null
  /** 알림 일시 중지 끝 시각. 지났거나 없으면 멈춰 있지 않다. */
  paused_until?: string | null
  /** 자리 비움으로 뒀다(3차 F41). */
  away?: boolean
  /** 상태의 날값 — 상태 설정 창이 이 값으로 선다. */
  status_parts?: StatusValue
}

export interface ThreadView {
  root: Line
  replies: readonly Line[]
  loading: boolean
  /** `also_channel` 이면 답글이 채널 본류에도 선다. 첨부는 글 뒤에 붙는다. */
  reply(written: string, also_channel: boolean, attached: readonly Attached[]): Promise<void>
  close(): void
  /** 이 스레드를 따르는가. 모르면 비워 둔다. */
  following?: boolean | undefined
  set_following?(on: boolean): void
  /** 이 스레드의 AI 요약. 없으면 단추도 없다. */
  summarize?(): Promise<AiDigest | null>
  /**
   * 답글 칸에 쓰다 만 글. 판을 닫았다 다시 열어도 남는다(3차 F167). 값이 아니라 읽는 손이다 — 스레드 자료는
   * 메모되어 있어서 값으로 실으면 다시 열 때 처음 연 순간의 글이 돌아왔다.
   */
  draft?(): string
  on_draft?(text: string): void
}

interface NavHands {
  switching(): void
  sheet(): void
  /** 왼쪽 길잡이의 판(읽지 않음·스레드·활동·나중에). 손이 없는 판은 열지 않는다. */
  pane(p: 'unreads' | 'threads' | 'activity' | 'saved'): void
  history: History
  moving: { current: boolean }
}

/** 창 단축키 하나를 실행한다. 판정은 `keymap.ts`, 여기는 무엇을 할지만. */
function run_nav(
  nav: Nav,
  live: { props: WorkspaceProps; pick(id: ChannelId): void },
  hands: NavHands,
) {
  const { props, pick } = live
  const order = props.channels.map((c) => c.id)
  const unread = (id: ChannelId) => (props.channels.find((c) => c.id === id)?.unread ?? 0) > 0
  let to: ChannelId | null = null
  switch (nav) {
    case 'switch':
      return hands.switching()
    case 'shortcuts':
      return hands.sheet()
    case 'unreads':
    case 'threads':
    case 'activity':
    case 'saved':
      return hands.pane(nav)
    case 'settings':
      return props.settings?.()
    case 'back':
    case 'forward':
      to = nav === 'back' ? hands.history.back() : hands.history.forward()
      if (to !== null) hands.moving.current = true
      break
    case 'prev_channel':
    case 'next_channel':
      to = step_channel(order, props.open, nav === 'prev_channel' ? -1 : 1)
      break
    case 'prev_unread':
    case 'next_unread':
      to = step_channel(order, props.open, nav === 'prev_unread' ? -1 : 1, unread)
      break
  }
  if (to !== null) pick(to)
}

/** 빠른 전환에 보일 것들. 채널이 먼저다 — 대부분의 이동은 채널로 간다. */
/**
 * 빠른 전환 목록. 이미 열린 1:1 대화방이 있는 사람은 한 번만 — 대화방 줄(얼굴 글자, 안 읽음 배지)로 선다.
 * 전엔 `# 이하늘`(채널 모양)과 사람 `이하늘` 이 둘 다 떴다(3차 F60).
 */
function switch_items_of(props: WorkspaceProps): SwitchItem[] {
  const talked = new Set(props.channels.filter((c) => c.dm === true).map((c) => c.name))
  return [
    ...props.channels.map((c) => ({
      kind: 'channel' as const,
      id: c.id,
      ...(c.unread > 0 ? { hint: String(c.unread) } : {}),
      ...(c.dm === true ? { dm: true } : {}),
      ...(c.secret === true ? { secret: true } : {}),
      name: c.name,
    })),
    ...(props.directory ?? [])
      .filter((p) => !talked.has(p.name))
      .map((p) => ({
        kind: 'person' as const,
        id: p.id,
        name: p.name,
      })),
  ]
}

/**
 * 사이드바 한 칸. 사용자가 지은 칸은 머리글을 눌러 접고, 채널을 끌어다 놓으면 옮긴다.
 * 기본 칸('채널')에 놓으면 칸에서 뺀다. 즐겨찾기는 놓는 자리가 아니다 — 별표가 따로 있다.
 */
function SidebarGroup(props: {
  part: SidebarPart<ChannelPick>
  open: ChannelId | null
  drafts?: ReadonlySet<ChannelId> | undefined
  huddles?: ReadonlyMap<ChannelId, readonly UserId[]> | undefined
  pick(id: ChannelId): void
  sections: readonly SectionSpec[]
  save?(list: readonly SectionSpec[]): Promise<void>
  move?(channel: ChannelId, section: string | null): Promise<void>
  /** 줄 메뉴를 연다. 없으면 … 단추도 없다. */
  menu?(id: ChannelId, anchor: HTMLElement): void
  /** 메뉴가 열린 줄. */
  menu_at?: ChannelId | null
}) {
  const { part, open, pick, save, move } = props
  const t = useT()
  const [over, set_over] = useState(false)
  const custom = part.custom === true
  const plain = !custom && (part.title === null || part.title === '채널')
  const target = move !== undefined && (custom || plain) ? move : undefined
  const rows = visible_rows(part, open)
  const hidden = part.channels.length - rows.length
  const fold = () => {
    if (save === undefined || part.title === null) return
    const name = part.title
    const known = props.sections.some((s) => s.name === name)
    const list = known
      ? props.sections.map((s) => (s.name === name ? { ...s, collapsed: !part.collapsed } : s))
      : [...props.sections, { name, collapsed: true }]
    void save(list)
  }
  // 섹션 머리를 끌어 다른 섹션 위에 놓으면 그 자리로 옮긴다(G34). 채널 끌기와 자료형이 달라 섞이지 않는다.
  const reorder = custom && save !== undefined && part.title !== null ? save : undefined
  const accepts = (e: React.DragEvent) =>
    (target !== undefined && e.dataTransfer.types.includes(CHANNEL_DRAG)) ||
    (reorder !== undefined && e.dataTransfer.types.includes(SECTION_DRAG))
  return (
    <div
      className={`part${over ? ' drop' : ''}${part.collapsed === true ? ' folded' : ''}`}
      {...(target === undefined && reorder === undefined
        ? {}
        : {
            onDragOver: (e: React.DragEvent) => {
              if (!accepts(e)) return
              e.preventDefault()
              set_over(true)
            },
            onDragLeave: () => set_over(false),
            onDrop: (e: React.DragEvent) => {
              set_over(false)
              const moved = e.dataTransfer.getData(SECTION_DRAG)
              if (moved !== '' && reorder !== undefined && part.title !== null) {
                e.preventDefault()
                if (moved !== part.title)
                  void reorder(move_section(props.sections, moved, part.title))
                return
              }
              const id = Number(e.dataTransfer.getData(CHANNEL_DRAG))
              if (target === undefined || !Number.isInteger(id) || id <= 0) return
              e.preventDefault()
              void target(id as ChannelId, custom ? part.title : null)
            },
          })}
    >
      {part.title !== null &&
        (custom && save !== undefined ? (
          <button
            type="button"
            className="part-name"
            aria-expanded={part.collapsed !== true}
            onClick={fold}
            // 머리는 끌 수 있다(G34). 키보드로는 설정의 ↑↓ 가 같은 일을 한다.
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(SECTION_DRAG, part.title ?? '')
              e.dataTransfer.effectAllowed = 'move'
            }}
          >
            <ChevronRight className="part-fold" size={12} aria-hidden />
            {part.emoji != null && <span className="part-emoji">{part.emoji}</span>}
            <span className="part-title">{part.title}</span>
            {hidden > 0 && (
              <span className="part-hidden" title={t('접혀서 숨은 채널 {n}개', { n: hidden })}>
                {hidden}
              </span>
            )}
          </button>
        ) : (
          <h3 className="part-name">
            {part.emoji != null && <span className="part-emoji">{part.emoji}</span>}
            {/* 기본 칸(즐겨찾기·채널)만 번역한다. 사람이 지은 이름은 그 사람의 말이다. */}
            <span className="part-title">{custom ? part.title : t(part.title)}</span>
          </h3>
        ))}
      {rows.map((c) => {
        const badge = badge_of(c)
        return (
          <ChannelRow
            key={c.id}
            id={c.id}
            name={c.name}
            unread={badge.unread}
            mention_count={badge.mention_count}
            selected={c.id === open}
            dm={c.dm === true}
            dm_with={c.dm_with}
            dm_self={c.dm_self}
            secret={c.secret === true}
            drafting={props.drafts?.has(c.id) === true}
            huddle={props.huddles?.has(c.id) === true}
            muted={c.notify === 'none'}
            draggable={move !== undefined}
            press={pick}
            {...(props.menu === undefined
              ? {}
              : { more: props.menu, more_open: props.menu_at === c.id })}
          />
        )
      })}
    </div>
  )
}

/**
 * 사이드바의 이동 줄(활동·스레드…). 채널 줄과 같은 높이·같은 들여쓰기라 한 목록으로 읽힌다.
 * 지금 오른쪽 판에 열린 것이면 옅게 칠한다 — 어디를 보고 있는지 한눈에.
 */
function SideLink(props: {
  icon: ReactNode
  on: boolean
  press(): void
  children: ReactNode
  /** 새로 온 것의 수. 0 이면 배지도 굵은 글씨도 없다. */
  news?: number | undefined
}) {
  const news = props.news ?? 0
  return (
    <Button
      variant="ghost"
      size="sm"
      className="SideLink"
      icon={props.icon}
      aria-current={props.on || undefined}
      data-news={news > 0 || undefined}
      onClick={props.press}
    >
      {props.children}
      {news > 0 && <Badge className="멘션배지" tone="danger" count={news} />}
    </Button>
  )
}

export function Workspace(props: WorkspaceProps) {
  const t = useT()
  // 늦은 청크 중 자주 누르는 것(옆 판 조립, 줄·나·채널 ⋯ 메뉴, 줄 위 손, 반응 고르기)은 한가할 때 미리
  // 받는다 — 첫 클릭이 청크를 기다리느라 한 박자 비면 "눌렀는데 반응 없음" 으로 읽힌다(3차 F47).
  // 첫 화면 크기에는 들지 않는다: 받는 때만 당길 뿐 여전히 따로 떨어진 청크다.
  useEffect(() => {
    const idle = globalThis.requestIdleCallback ?? ((run: () => void) => setTimeout(run, 1500))
    idle(() => {
      void import('./manage_side.tsx')
      void import('./row_menu.tsx')
      void import('./message_more.tsx')
      void import('./emoji_pick.tsx')
    })
  }, [])
  const here = props.channels.find((c) => c.id === props.open)
  const [panel, set_panel] = useState<Panel>('people')
  // 그 채널에 매인 판(초대·채널 설정·알림 설정·전달)은 채널을 옮기면 닫는다. 대화방에서 연 "사람 더하기" 가
  // 채널로 옮긴 뒤 "채널에 초대" 로 바뀐 채 남아 있었다(3차 F137). 목록 판(검색·활동…)과 캔버스는 둔다.
  // 창으로 뜬 판(`MODAL`). 채널에 매인 창도 채널을 옮기면 닫는다.
  const [modal, set_modal] = useState<Panel | null>(null)
  const [panel_for, set_panel_for] = useState(props.open)
  if (panel_for !== props.open) {
    set_panel_for(props.open)
    if (BOUND.has(panel)) set_panel('people')
    if (modal !== null && BOUND.has(modal)) set_modal(null)
  }
  // 새로 선 스레드는 판을 비키게 한다 — 스레드는 기본 판 위에만 서서, 검색·활동에서 답글로 뛰면 스레드가 열려도
  // 검색 판에 가려 아무 일도 없어 보였다(3차 F340).
  const thread_seq = props.thread?.root.seq ?? null
  const [thread_was, set_thread_was] = useState(thread_seq)
  if (thread_was !== thread_seq) {
    set_thread_was(thread_seq)
    if (thread_seq !== null) set_panel('people')
  }
  // 좁은 화면(전화)에서는 목록과 대화 중 하나만 보인다. 넓은 화면은 이 값을 무시한다 —
  // CSS 가 `@media` 안에서만 읽는다. 처음엔 목록이다: 전화를 켜자마자 아무 채널에나
  // 떨어지는 것보다 고르게 하는 편이 낫다.
  const [showing, set_showing] = useState<'list' | 'talk'>('list')
  // 검색에서 뛰어온 줄. 한 번 쓰고 비우지 않는다 — 비우면 그 줄이 곧장 평범해진다. 채널까지 적어 둔다 —
  // 번호만 들면 [새 메시지]·사이드바 밖의 길로 다른 방에 가도 같은 번호의 줄이 뛴 줄로 빛났다(3차 F310).
  const [focus_at, set_focus] = useState<{ channel: ChannelId; seq: Seq } | null>(null)
  const focus = focus_at !== null && focus_at.channel === props.open ? focus_at.seq : null
  const [who, set_who] = useState<UserId | null>(null)
  // 전달할 줄. 채널을 고르는 동안만 들고 있다 — 고르고 나면 비운다.
  const [forwarding, set_forwarding] = useState<Seq | null>(null)
  const forward_hand =
    props.manage?.forward === undefined
      ? {}
      : {
          on_forward: (seq: Seq) => {
            set_forwarding(seq)
            set_modal('forward')
          },
        }
  const [switching, set_switching] = useState(false)
  // 모두 읽음은 되돌릴 단추가 없다(줄마다 '안 읽음으로 표시' 뿐) — 창으로 한 번 묻는다(F15).
  const [clearing, set_clearing] = useState(false)
  const unread_rooms = props.channels.filter((c) => c.unread > 0 || c.mention_count > 0).length
  const view = props.sidebar_view ?? SIDEBAR_VIEW_DEFAULT
  // 칸마다 보기대로 거르고 늘어놓는다. "안 읽은 것만" 이면 빈 칸은 머리글째 뺀다 — 빈 머리글만 줄지어 서면
  // 걸러진 게 아니라 고장으로 읽힌다.
  const parts = group_channels(props.channels, props.sections ?? [])
    .map((part) => ({ ...part, channels: arrange_rows(part.channels, view, props.open) }))
    .filter((part) => !view.unread_only || part.channels.length > 0)
  const [sheet, set_sheet] = useState(false)
  // 채널 방문 기록(⌘[ ⌘]). 기록을 따라 옮길 때는 다시 적지 않는다.
  const history = useRef(new History())
  const moving = useRef(false)
  useEffect(() => {
    if (props.open === null) return
    if (moving.current) moving.current = false
    else history.current.visit(props.open)
  }, [props.open])
  const pick = (id: ChannelId) => {
    props.pick(id)
    set_focus(null)
    set_showing('talk')
  }
  // 창 전체에서 듣는다 — 어디에 포커스가 있든 열려야 한다. 듣개는 한 번만 달고
  // 최신 props 는 ref 로 읽는다.
  const live = useRef({ props, pick })
  live.current = { props, pick }
  useEffect(() => {
    const press = (e: KeyboardEvent) => {
      const nav = nav_of_key({
        key: e.key,
        mod: e.metaKey || e.ctrlKey,
        shift: e.shiftKey,
        alt: e.altKey,
      })
      if (nav === null) return
      e.preventDefault()
      run_nav(nav, live.current, {
        switching: () => set_switching((on) => !on),
        sheet: () => set_sheet((on) => !on),
        pane: (p) => {
          const m = live.current.props.manage
          const hand = {
            unreads: m?.unreads,
            threads: m?.my_threads,
            activity: m?.activity,
            saved: m?.saved_marks,
          }
          if (hand[p] === undefined) return
          set_showing('talk')
          set_panel(p)
        },
        history: history.current,
        moving,
      })
    }
    globalThis.addEventListener?.('keydown', press)
    return () => globalThis.removeEventListener?.('keydown', press)
  }, [])
  const jump = (id: ChannelId, seq: Seq) => {
    props.pick(id)
    set_showing('talk')
    // 목록 판(검색·활동·나중에…)에서 뛰면 그 판을 둔다 — 다음 결과를 보려고 다시 검색하게 하지 않는다(3차 F62).
    // 좁은 화면에선 판이 화면을 덮으므로 닫아야 뛴 줄이 보인다.
    set_panel((p) => (STAYS.has(p) && !narrow() ? p : 'people'))
    if (props.jump === undefined) {
      set_focus(null)
      return
    }
    // 못 닿으면 강조하지 않는다. 엉뚱한 줄을 가리키느니 아무것도 안 가리킨다.
    void props.jump(id, seq).then((ok) => set_focus(ok ? { channel: id, seq } : null))
  }
  // 스레드 모아 보기에서 연다 — 그 채널로 뛰고 옆 자리에 그 스레드를 세운다(3차 F185).
  const open_thread = (id: ChannelId, seq: Seq) => {
    jump(id, seq)
    set_panel('people')
    props.on_thread?.(seq, id)
  }
  // 바깥에서 온 뛰기. 효과 사건으로 두어 `jump` 가 새로 만들어져도 지난 좌표로 다시 뛰지 않는다.
  const aimed = useEffectEvent((at: { channel: ChannelId; seq: Seq }) => jump(at.channel, at.seq))
  useEffect(() => {
    if (props.aim != null) aimed(props.aim)
  }, [props.aim])
  const show_who = (user: UserId) => {
    set_who(user)
    set_panel('who')
  }
  const to_list = () => {
    set_showing('list')
    set_people_up(false)
    // 누른 ← 가 대화째 가려져 초점이 바닥에 떨어졌다(3차 F298). 목록이 서면 방금 보던 채널의 줄로 — 거기서
    // 방향키로 옆 채널을 고른다.
    requestAnimationFrame(() => {
      const now = document.activeElement
      if (now !== null && now !== document.body && now.checkVisibility?.() !== false) return
      document.querySelector<HTMLElement>('.Workspace nav [aria-current="page"]')?.focus()
    })
  }
  // 좁은 화면에서 구성원 판을 **불러 세웠는가**. 넓은 화면에선 구성원 판이 쉬는 자리라 늘 보이지만, 전화에선
  // 쉬는 자리를 숨기므로 머리의 구성원 수를 눌러도 아무것도 안 떴다(3차 F86). 부르면 덮는 판으로, 닫기와 함께.
  const [people_up, set_people_up] = useState(false)
  const show = (p: Panel) => {
    if (MODAL.has(p)) {
      set_modal(p)
      return
    }
    set_panel(p)
    set_people_up(p === 'people' && narrow())
  }
  // 명단에서 연 쪽지창을 닫으면 명단으로 돌아간다 — 전화에서 대화로 떨어지면 다음 사람을 보려고 다시 불러야 했다(3차 F146).
  const back = () => {
    set_panel('people')
    set_people_up(panel === 'who' && people_up)
    // 저장하며 닫히는 판(알림·채널 설정·새 채널 …)은 누른 단추가 판째 사라져 초점이 바닥에 떨어졌다(3차 F294).
    // 판이 빠진 다음 틀에서도 바닥이면 Esc 와 같이 대화의 입력칸으로.
    requestAnimationFrame(() => {
      const now = document.activeElement
      if (now !== null && now !== document.body && now.isConnected) return
      document.querySelector<HTMLTextAreaElement>('.Workspace > section textarea')?.focus()
    })
  }
  // Esc 로 옆 판을 닫는다 — 머리의 닫기 단추를 누른 것과 같다(3차 F191). 초점이 판 안에 있거나 아무 데도 없을
  // 때만이다(이름을 눌러 연 쪽지창처럼 누른 단추가 사라지면 초점이 문서로 떨어진다). 판 속의 메뉴·창·자동완성이
  // 먼저 먹고(`defaultPrevented`·열린 popover), 찾기 칸에 글이 있으면 브라우저가 먼저 글을 비운다. 닫기 단추가
  // 없는 판(사이드바 줄로 여닫는 활동·나중에 …)은 그대로 둔다. 닫은 뒤 초점은 대화의 입력칸으로.
  const side = useRef<HTMLElement>(null)
  const close_on_escape = useEffectEvent((e: KeyboardEvent) => {
    if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return
    const at = e.target
    const box = side.current
    if (box === null || !(at instanceof Node)) return
    if (!box.contains(at) && at !== document.body) return
    if (document.querySelector('dialog[open], [popover="auto"]:popover-open') !== null) return
    if (at instanceof HTMLInputElement && at.type === 'search' && at.value !== '') return
    const shut = box.querySelector<HTMLButtonElement>('.PanelHead .close')
    if (shut === null) return
    e.preventDefault()
    shut.click()
    document.querySelector<HTMLTextAreaElement>('.Workspace > section textarea')?.focus()
  })
  useEffect(() => {
    const listen = (e: KeyboardEvent) => close_on_escape(e)
    window.addEventListener('keydown', listen)
    return () => window.removeEventListener('keydown', listen)
  }, [])
  // 메뉴가 열린 줄과 그 … 단추. 목록 하나가 메뉴 하나를 쓴다.
  const [row_at, set_row_at] = useState<{ id: ChannelId; el: HTMLElement } | null>(null)
  const hands = props.manage?.row
  const row_c = row_at === null ? undefined : props.channels.find((c) => c.id === row_at.id)
  // 내 자리 메뉴가 붙은 단추. 열려 있을 때만 있다.
  const [me_at, set_me_at] = useState<HTMLElement | null>(null)
  // 상태 설정 창이 열려 있는가.
  const [status_open, set_status_open] = useState(false)
  // 워크스페이스 메뉴가 붙은 이름 단추.
  const [org_at, set_org_at] = useState<HTMLElement | null>(null)
  const self = props.self

  // ⌘K 의 동작 줄(G27). 목록(글자)은 ⌘K 청크가 짓는다 — 여기는 손만 건넨다(첫 화면을 가볍게).
  const org = props.org
  const action_hands: ActionHands = {
    show: (p) => show(p as Panel),
    sheet: () => set_sheet(true),
    ...(props.manage === undefined ? {} : { manage: true }),
    ...(props.manage?.search_files === undefined ? {} : { files: true }),
    ...(props.settings === undefined ? {} : { settings: props.settings }),
    ...(props.set_sidebar_view === undefined
      ? {}
      : {
          unread_only: {
            on: view.unread_only,
            toggle: () => props.set_sidebar_view?.({ ...view, unread_only: !view.unread_only }),
          },
        }),
    ...(props.manage?.read_all === undefined || unread_rooms === 0
      ? {}
      : { clear: () => set_clearing(true) }),
    // ⌘[ ⌘] 의 눈에 보이는 자리. 기록을 따라 옮기는 것이라 다시 적지 않는다(`moving`).
    back: () => {
      const to = history.current.back()
      if (to === null) return
      moving.current = true
      pick(to)
    },
    forward: () => {
      const to = history.current.forward()
      if (to === null) return
      moving.current = true
      pick(to)
    },
    ...(org === undefined
      ? {}
      : { orgs: org.others, switch_org: (tenant: number) => void org.switch(tenant) }),
  }
  const switch_items = switch_items_of(props)

  return (
    <div className={`Workspace ${showing}`}>
      {hands !== undefined && row_at !== null && row_c !== undefined && (
        <Suspense fallback={null}>
          <RowMenuAt
            key={row_c.id}
            c={row_c}
            anchor={row_at.el}
            hands={hands}
            sections={props.sections ?? []}
            {...(props.manage?.move_to_section === undefined
              ? {}
              : { move: props.manage.move_to_section })}
            {...(props.manage?.save_sections === undefined
              ? {}
              : { save: props.manage.save_sections })}
            {...(props.manage?.set_pref === undefined
              ? {}
              : {
                  // 알림 판은 연 채널의 것이다 — 그 채널을 열고 판을 띄운다.
                  // 판의 주인도 그 채널로 함께 적는다 — 안 적으면 채널을 옮겼다고 보고 방금 연 판을 닫는다(3차 F321).
                  notify: () => {
                    pick(row_c.id)
                    set_panel_for(row_c.id)
                    set_modal('pref')
                  },
                })}
            close={() => set_row_at(null)}
          />
        </Suspense>
      )}
      {props.org !== undefined && org_at !== null && (
        <Suspense fallback={null}>
          <OrgMenuAt anchor={org_at} org={props.org} close={() => set_org_at(null)} />
        </Suspense>
      )}
      {self !== undefined && me_at !== null && (
        <Suspense fallback={null}>
          <MeMenuAt
            name={self.name}
            anchor={me_at}
            hands={{
              ...(props.settings === undefined ? {} : { settings: props.settings }),
              ...(props.set_status === undefined ? {} : { status: () => set_status_open(true) }),
              ...(props.profile_of === undefined ? {} : { profile: () => show_who(props.me) }),
              shortcuts: () => set_sheet(true),
              ...(props.sign_out === undefined ? {} : { sign_out: props.sign_out }),
              ...(props.snooze === undefined
                ? {}
                : { snooze: { until: self.paused_until ?? null, set: props.snooze } }),
              ...(props.set_away === undefined
                ? {}
                : { away: { on: self.away === true, set: props.set_away } }),
            }}
            close={() => set_me_at(null)}
          />
        </Suspense>
      )}
      {self !== undefined && status_open && props.set_status !== undefined && (
        <Suspense fallback={null}>
          <StatusDialog
            value={self.status_parts ?? { emoji: null, text: null, until: null }}
            save={props.set_status}
            close={() => set_status_open(false)}
          />
        </Suspense>
      )}
      <Suspense fallback={null}>
        {sheet && <ShortcutSheet close={() => set_sheet(false)} />}
        {switching && (
          <QuickSwitch
            items={switch_items}
            hands={action_hands}
            close={() => set_switching(false)}
            pick={(item) => {
              set_switching(false)
              if (item.kind === 'channel') pick(item.id as ChannelId)
              else void props.manage?.open_dm([item.id as UserId])
            }}
          />
        )}
      </Suspense>
      {props.manage?.read_all !== undefined && (
        <ConfirmDialog
          open={clearing}
          on_close={() => set_clearing(false)}
          title={t('모두 읽음으로 표시할까요?')}
          description={t(
            '안 읽은 채널 {n}곳이 모두 읽음이 돼요. 되돌리려면 메시지마다 안 읽음으로 다시 표시해야 해요.',
            { n: unread_rooms },
          )}
          confirm_label={t('모두 읽음으로 표시')}
          confirm_variant="primary"
          act={() => props.manage?.read_all?.() ?? Promise.resolve()}
        />
      )}
      <nav className="채널목록" aria-label={t('채널')}>
        {props.manage !== undefined && (
          <>
            {/* 조직 이름과 검색은 목록을 내려도 위에 붙어 있다 — 슬랙과 같다(3차 F279). */}
            <div className="side-top">
              {props.org !== undefined && (
                <button
                  type="button"
                  className="side-org"
                  aria-haspopup="menu"
                  aria-expanded={org_at !== null}
                  onClick={(e) => set_org_at(e.currentTarget)}
                >
                  <span>{props.org.name}</span>
                  <ChevronDown size={14} aria-hidden />
                </button>
              )}
              <div className="side-head">
                <Button
                  size="sm"
                  className="side-search"
                  icon={<Search size={14} aria-hidden />}
                  onClick={() => show('search')}
                >
                  <span>{t('검색')}</span>
                </Button>
                {props.manage.read_all !== undefined && (
                  <IconButton
                    size="sm"
                    icon={<CheckCheck aria-hidden />}
                    label={
                      unread_rooms === 0 ? t('안 읽은 메시지가 없어요') : t('모두 읽음으로 표시')
                    }
                    disabled={unread_rooms === 0}
                    onClick={() => set_clearing(true)}
                  />
                )}
                {props.set_sidebar_view !== undefined && (
                  <IconButton
                    size="sm"
                    icon={<ListFilter aria-hidden />}
                    label={t('안 읽은 채널만 보기')}
                    pressed={view.unread_only}
                    onClick={() =>
                      props.set_sidebar_view?.({ ...view, unread_only: !view.unread_only })
                    }
                  />
                )}
                <IconButton
                  size="sm"
                  icon={<ArrowLeftRight aria-hidden />}
                  label={t('빠른 전환')}
                  shortcut="⌘K"
                  onClick={() => set_switching(true)}
                />
              </div>
            </div>
            <div className="side-links">
              {props.manage.activity !== undefined && (
                <SideLink
                  icon={<Bell />}
                  on={panel === 'activity'}
                  news={props.activity_news?.count}
                  press={() => show('activity')}
                >
                  {t('활동')}
                </SideLink>
              )}
              {props.manage.unreads !== undefined && (
                <SideLink icon={<Inbox />} on={panel === 'unreads'} press={() => show('unreads')}>
                  {t('읽지 않음')}
                </SideLink>
              )}
              {props.manage.my_threads !== undefined && (
                <SideLink
                  icon={<MessagesSquare />}
                  on={panel === 'threads'}
                  press={() => show('threads')}
                >
                  {t('스레드')}
                </SideLink>
              )}
              {props.manage.saved_marks !== undefined && (
                <SideLink
                  icon={<BookmarkIcon />}
                  on={panel === 'saved'}
                  press={() => show('saved')}
                >
                  {t('나중에')}
                </SideLink>
              )}
              {props.all_dms !== undefined && (
                // 닫은 대화방까지 찾는 자리(G33) — 사이드바의 대화방 칸은 닫지 않은 것만이다.
                <SideLink icon={<UsersRound />} on={panel === 'dms'} press={() => show('dms')}>
                  {t('모든 대화')}
                </SideLink>
              )}
              {props.drafts !== undefined && (
                // 초안 모아 보기(G28). 늘 선다 — 쓰다 만 글이 있을 때만 서면 첫 글자를 치는 순간
                // 사이드바 전체가 한 줄 밀리고, 보내면 다시 당겨진다(F317). 수는 있을 때만.
                <SideLink
                  icon={<NotebookPen />}
                  on={panel === 'drafts'}
                  press={() => show('drafts')}
                >
                  {t('초안')}
                  {props.drafts.size > 0 && (
                    <span className="SideLink-count">{props.drafts.size}</span>
                  )}
                </SideLink>
              )}
              {props.manage.search_files !== undefined && (
                // 파일 모아 보기(G29) — 검색 속 탭으로만 있으면 "파일을 어디서 보지" 를 못 찾는다.
                <SideLink icon={<Files />} on={panel === 'files'} press={() => show('files')}>
                  {t('파일')}
                </SideLink>
              )}
              {props.manage.bookings !== undefined && (
                <SideLink
                  icon={<CalendarClock />}
                  on={panel === 'scheduled'}
                  press={() => show('scheduled')}
                >
                  {t('예약')}
                </SideLink>
              )}
              {props.manage.browse !== undefined && (
                <SideLink icon={<Compass />} on={panel === 'browse'} press={() => show('browse')}>
                  {t('채널 둘러보기')}
                </SideLink>
              )}
            </div>
            {props.starter !== undefined && (
              <Suspense fallback={null}>
                <GettingStarted
                  steps={props.starter.steps.map((s) => ({
                    ...s,
                    go: s.go ?? (() => show('new')),
                  }))}
                  dismiss={props.starter.dismiss}
                />
              </Suspense>
            )}
          </>
        )}
        {view.unread_only && unread_rooms === 0 && (
          // 다 읽었으면 빈 사이드바가 아니라 그 사실을 말하고, 돌아갈 길을 준다(G25). 보고 있는 채널은
          // 걸러도 남으므로 "칸이 비었는가" 로 보면 안내가 영영 안 섰다(3차 F277).
          <div className="SideCaughtUp" role="status">
            <p>{t('안 읽은 채널이 없어요')}</p>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => props.set_sidebar_view?.({ ...view, unread_only: false })}
            >
              {t('모든 채널 보기')}
            </Button>
          </div>
        )}
        {parts.map((part) => (
          <SidebarGroup
            key={part.title ?? '_'}
            part={part}
            open={props.open}
            drafts={props.drafts}
            huddles={props.manage?.huddle?.live}
            pick={pick}
            sections={props.sections ?? []}
            {...(props.manage?.save_sections === undefined
              ? {}
              : { save: props.manage.save_sections })}
            {...(props.manage?.move_to_section === undefined
              ? {}
              : { move: props.manage.move_to_section })}
            {...(hands === undefined
              ? {}
              : {
                  menu: (id: ChannelId, el: HTMLElement) => set_row_at({ id, el }),
                  menu_at: row_at?.id ?? null,
                })}
          />
        ))}
        {props.manage !== undefined && (
          <div className="side-links side-add">
            <SideLink icon={<Plus />} on={modal === 'new'} press={() => show('new')}>
              {t('채널 추가')}
            </SideLink>
            <SideLink icon={<SquarePen />} on={modal === 'dm'} press={() => show('dm')}>
              {t('새 메시지')}
            </SideLink>
            {props.manage.save_sections !== undefined && (
              <SideLink
                icon={<SlidersHorizontal />}
                on={panel === 'sections'}
                press={() => show('sections')}
              >
                {t('섹션 관리')}
              </SideLink>
            )}
          </div>
        )}
        <div className="side-foot">
          {props.manage?.huddle !== undefined &&
            (props.manage.huddle.mine !== null || props.manage.huddle.error !== null) && (
              <Suspense fallback={null}>
                <HuddleDock
                  hands={props.manage.huddle}
                  name_of={props.name_of}
                  channel_name={(id) => props.channels.find((c) => c.id === id)?.name}
                />
              </Suspense>
            )}
          {self !== undefined && (
            <button
              type="button"
              className="MePod"
              aria-haspopup="menu"
              aria-expanded={me_at !== null}
              title={t('내 메뉴')}
              onClick={(e) => set_me_at(e.currentTarget)}
            >
              <span className="MePod-face">
                <Avatar
                  name={self.name}
                  {...(self.id === undefined ? {} : { user: self.id })}
                  size="sm"
                  decorative
                />
                {/* 내 초록불 — 자리 비움이면 속이 빈 회색(색과 모양 둘 다로 말한다). */}
                <span
                  className="MePod-dot"
                  data-away={self.away === true || undefined}
                  role="img"
                  aria-label={self.away === true ? t('자리 비움') : t('접속 중')}
                />
              </span>
              <span className="MePod-text">
                <span className="MePod-name">{self.name}</span>
                {self.status !== null && <span className="MePod-status">{self.status}</span>}
              </span>
              {snoozed(self.paused_until) && (
                // 아이콘 모듈 대신 글자 — 첫 청크에 아이콘 하나를 더 싣지 않는다(번들 상한).
                <span className="MePod-paused" role="img" aria-label={t('알림 일시 중지 중')}>
                  🔕
                </span>
              )}
            </button>
          )}
        </div>
      </nav>
      <section className="타임라인" aria-label={here ? here.name : t('대화')}>
        {here === undefined ? (
          <NoConversation {...props} show={show} />
        ) : (
          <Conversation
            {...props}
            here={here}
            show={show}
            panel={modal ?? panel}
            to_list={to_list}
            focus={focus}
            {...(props.profile_of === undefined ? {} : { on_who: show_who })}
            // 스레드는 옆 자리의 기본 판 위에 선다. 다른 판(검색·핀…)이 열려 있으면 그 판이 이겨 눌러도 안 뜨므로
            // 판을 비키게 한다.
            {...(props.on_thread === undefined
              ? {}
              : {
                  on_thread: (seq: Seq) => {
                    set_panel('people')
                    props.on_thread?.(seq)
                  },
                })}
            {...forward_hand}
          />
        )}
      </section>
      <aside className="side" ref={side} data-panel={panel ?? undefined}>
        {/* 옆 판 청크를 처음 받는 동안에도 판 자리는 선다 — `null` 이면 가운데가 넓어졌다 다시 좁아져 읽던
            줄이 옆으로 튄다(3차 F324). 기다림은 처음 한 번뿐이다. */}
        <Suspense
          fallback={
            <div className="Pane">
              <div className="Pane-body">
                <SkeletonText lines={4} />
              </div>
            </div>
          }
        >
          <Side
            {...props}
            // 답글도 전달한다 — 스레드 판의 줄 메뉴가 본류와 같은 손을 받는다(3차 F228).
            {...forward_hand}
            panel={panel}
            back={back}
            people_up={people_up}
            on_jump={jump}
            open_thread={open_thread}
            who={who}
            show_who={show_who}
            forwarding={forwarding}
            show={show}
          />
        </Suspense>
      </aside>
      {modal !== null && props.manage !== undefined && (
        <Suspense fallback={null}>
          <Side
            {...props}
            panel={modal}
            modal
            back={() => set_modal(null)}
            on_jump={jump}
            who={null}
            show_who={show_who}
            forwarding={forwarding}
            show={show}
          />
        </Suspense>
      )}
    </div>
  )
}

/** 연 대화가 없을 때의 자리. 채널이 있는데 "없다" 고 말하면 거짓이다 — 고르라고 한다. */
function NoConversation(props: WorkspaceProps & { show(p: Panel): void }) {
  const t = useT()
  const { manage, show } = props
  // 목록을 받기 전에는 아무 말도 안 한다 — 새로 고칠 때마다 "아직 참여한 채널이 없어요" 가 번쩍였다(3차 F266).
  if (props.channels_loading === true) return null
  if (props.channels.length > 0) {
    return (
      <EmptyState
        icon={<MessagesSquare />}
        title={t('채널을 골라 주세요')}
        description={t('왼쪽 목록에서 채널이나 DM을 고르면 여기에 대화가 열려요.')}
      />
    )
  }
  if (manage === undefined) {
    return (
      <EmptyState
        icon={<MessagesSquare />}
        title={t('아직 참여한 채널이 없어요')}
        description={t('초대를 받았다면 새로 고침해 보세요.')}
      />
    )
  }
  return (
    <EmptyState
      icon={<MessagesSquare />}
      title={t('아직 참여한 채널이 없어요')}
      description={t('채널을 찾아 참여하거나 새로 만들어 보세요.')}
      action={
        <>
          {manage.browse !== undefined && (
            <Button
              variant="secondary"
              icon={<Compass size={LEAD_SIZE.md} aria-hidden />}
              onClick={() => show('browse')}
            >
              {t('채널 둘러보기')}
            </Button>
          )}
          <Button
            variant="primary"
            icon={<Plus size={LEAD_SIZE.md} aria-hidden />}
            onClick={() => show('new')}
          >
            {t('채널 만들기')}
          </Button>
        </>
      }
    />
  )
}

/** 오른쪽 판. 스레드가 열려 있으면 그것이 우선한다 — 자리는 하나다. */
/** 스레드 판 머리의 자리 이름 — 채널은 `#이름`, 대화는 사람 이름 그대로. */
function thread_place(c: ChannelPick | undefined): string | undefined {
  if (c === undefined || c.name === '') return undefined
  return c.dm === true ? c.name : `#${c.name}`
}

function Side(props: SideProps) {
  const { manage, directory, back } = props
  const who_side = who_panel(props)
  if (who_side !== null) return who_side
  if (manage !== undefined && props.panel !== 'people') {
    const here = props.channels.find((c) => c.id === props.open)
    return (
      // 닫은 대화방은 사이드바 목록(`channels`)에 없다 — `all_dms` 로 한 번 더 봐야 검색·활동 줄에서
      // `# 박도윤` 처럼 채널로 적히지 않는다(3차 F323).
      <PlaceKinds
        value={(id) =>
          place_kind(props.channels.find((c) => c.id === id)) ??
          (props.all_dms?.some((d) => d.id === id) === true ? 'dm' : undefined)
        }
      >
        <ManageSide
          {...props}
          manage={manage}
          directory={directory ?? []}
          back={back}
          {...(here === undefined ? {} : { here })}
        />
      </PlaceKinds>
    )
  }
  if (props.open === null) return null
  if (props.thread !== undefined) {
    return (
      <ThreadPanel
        root={props.thread.root}
        place={thread_place(props.channels.find((c) => c.id === props.open))}
        replies={props.thread.replies}
        loading={props.thread.loading}
        name_of={props.name_of}
        bot_of={props.bot_of}
        group_of={props.group_of}
        emoji_of={props.emoji_of}
        me={props.me}
        reply={props.thread.reply}
        {...(props.thread.draft === undefined ? {} : { draft: props.thread.draft })}
        {...(props.thread.on_draft === undefined ? {} : { on_draft: props.thread.on_draft })}
        close={props.thread.close}
        following={props.thread.following}
        {...(props.thread.set_following === undefined
          ? {}
          : { set_following: props.thread.set_following })}
        {...(props.thread.summarize === undefined
          ? {}
          : { summarize: props.thread.summarize, jump: props.on_jump })}
        {...(props.files === undefined ? {} : { files: props.files })}
        {...(props.people === undefined ? {} : { people: props.people })}
        {...(props.attach === undefined ? {} : { attach: props.attach })}
        hands={props}
        tallies={props.tallies}
        pinned={props.pinned}
        saved={props.saved}
      />
    )
  }
  // 명단을 기다리는 동안에도 판은 선다(F322) — `people` 이 없으면 자리표시다.
  return (
    <PeoplePanel
      people={props.people}
      {...(props.people_up === true ? { close: back } : {})}
      {...(props.profile_of === undefined ? {} : { pick: props.show_who })}
    />
  )
}

/** `who` 판을 그릴 수 있으면 그린다. 아니면 `null` — 부르는 쪽이 다음 갈래로 간다. */
function who_panel(props: SideProps) {
  const { manage, who, profile_of } = props
  if (props.panel !== 'who' || who === null || profile_of === undefined) return null
  const mine = who === props.me
  const seen = props.people?.find((p) => p.id === who)
  // 이미 그 사람과의 방에 있으면 [DM 열기] 는 제자리 걸음이다 — 눌러도 아무 일도 없어 보였다(3차 F260).
  const here = props.channels.find((c) => c.id === props.open)
  const in_dm = here?.dm_with?.length === 1 && here.dm_with[0] === who
  return (
    <WhoSide
      who={who}
      online={seen?.online}
      name={props.name_of(who) ?? `#${who}`}
      load={profile_of}
      close={props.back}
      {...(manage === undefined || mine || in_dm
        ? {}
        : { open_dm: () => void manage.open_dm([who]) })}
      {...(mine && props.settings !== undefined ? { edit: () => props.settings?.('profile') } : {})}
    />
  )
}

interface SideProps extends WorkspaceProps {
  panel: Panel
  /** 판을 창으로 띄운다(`MODAL`). */
  modal?: boolean
  back(): void
  /** 좁은 화면에서 구성원 판을 불러 세웠다 — 닫기를 달고 덮는 판으로 그린다. */
  people_up?: boolean
  on_jump(channel: ChannelId, seq: Seq): void
  /** 스레드 모아 보기의 "스레드 열기". 없으면 뿌리로 뛰기만 한다. */
  open_thread?: ((channel: ChannelId, seq: Seq) => void) | undefined
  who: UserId | null
  show_who(user: UserId): void
  forwarding?: Seq | null
  /** 다른 판을 연다(둘러보기의 빈 목록 → 채널 만들기). */
  show(p: Panel): void
}

/** 한 사람을 받아 그린다. 받아 오는 일이 여기 있는 이유: 판을 열 때만 묻는다. */
/** 이름 곁의 별표. 눌린 상태를 `aria-pressed` 로 읽힌다 — 모양만 바뀌면 안 보이는 사람은 모른다. */
function StarButton({ here, hands }: { here: ChannelPick; hands: RowHands }) {
  const t = useT()
  const on = here.favorite === true
  return (
    <IconButton
      className="RoomHead-star"
      size="sm"
      label={on ? t('즐겨찾기에서 제거') : t('즐겨찾기에 추가')}
      pressed={on}
      data-on={on || undefined}
      icon={<Star aria-hidden />}
      onClick={() => void hands.pref(here.id, { favorite: !on })}
    />
  )
}

/** 채널 머리 ⋯ 메뉴의 항목들 — 머리에 늘 서 있을 만큼 자주 쓰지는 않는 판들. */
function room_items(
  manage: Manage,
  show: (p: Panel) => void,
  t: (ko: string) => string,
  dm: boolean,
): RoomMenuItem[] {
  const item = (key: RoomMenuItem['key'], label: string): RoomMenuItem => ({
    key,
    label: t(label),
    on_select: () => show(key),
  })
  return [
    ...(manage.summarize === undefined ? [] : [item('summary', msg('채널 요약'))]),
    ...(manage.pins === undefined ? [] : [item('pins', msg('고정된 항목'))]),
    ...(manage.lists === undefined ? [] : [item('lists', msg('리스트'))]),
    ...(manage.set_pref === undefined ? [] : [item('pref', msg('알림 설정'))]),
    // 대화방은 이름도 주제도 없다 — 사람이 곧 정체성이라 고칠 것이 없다(서버도 거절한다).
    ...(manage.edit_channel === undefined || dm ? [] : [item('edit', msg('채널 설정'))]),
  ]
}

/** 채널 머리글 — 이름·주제와 손이 닿는 단추들. 본문에서 갈라 두면 둘 다 읽힌다. */
function RoomHead({
  here,
  to_list,
  show,
  panel,
  manage,
  count,
  people,
  pick_person,
  pinned,
}: {
  /** 이 채널에 고정한 글의 수. 모르면(아직 안 받음) 없다. */
  pinned?: number | undefined
  here: ChannelPick
  to_list(): void
  show(p: Panel): void
  /** 구성원 수. 모르면(아직 안 받음) 단추를 안 그린다. */
  count?: number | undefined
  /** 세부정보 창의 구성원 탭. */
  people?: readonly Person[] | undefined
  /** 세부정보 창에서 사람을 누르면 쪽지창. */
  pick_person?: ((user: UserId) => void) | undefined
  /** 지금 오른쪽에 열린 판 — 그 판을 여는 도구는 펼친 채로 읽힌다(`aria-expanded`). */
  panel: Panel
  manage?: Manage
}) {
  const t = useT()
  // ⋯ 메뉴가 곁에 설 단추. 열려 있을 때만 값이 있다.
  const [more, set_more] = useState<HTMLElement | null>(null)
  // 세부정보 창과 처음 열 탭. 닫혀 있으면 `null`.
  const [details, set_details] = useState<DetailsTab | null>(null)
  // 도구는 아이콘 단추다 — 이름은 `aria-label` 과 풍선에 산다(IconButton). 머리글이 한 줄을 넘으면
  // 타임라인이 위아래로 흔들린다.
  // 이름·주제가 곧 채널 세부정보의 문이다(G21) — 고치기·구성원·알림이 탭으로 한 자리에 모인다.
  // 대화방은 고칠 것도 탭도 없다(`room_items` 와 같은 판단).
  const open_edit =
    manage === undefined || here.dm === true ? undefined : () => set_details('about')
  // 창이 여는 판 — 손이 있는 것만.
  const can = new Set<DetailsGo>([
    ...(manage?.set_pref === undefined ? [] : (['pref'] as const)),
    ...(manage?.pins === undefined ? [] : (['pins'] as const)),
    ...(manage?.lists === undefined ? [] : (['lists'] as const)),
    ...(manage?.canvas === undefined ? [] : (['canvas'] as const)),
    ...(manage?.summarize === undefined ? [] : (['summary'] as const)),
    ...(manage?.edit_channel === undefined ? [] : (['edit'] as const)),
    ...(manage?.search_files === undefined ? [] : (['channel_files'] as const)),
    'invite' as const,
  ])
  const title = (
    <>
      {here.dm === true ? (
        // 대화방은 채널이 아니다 — `#` 대신 사이드바와 같은 얼굴 글자(3차 J10).
        <DmFace
          className="RoomHead-face"
          name={here.name}
          with={here.dm_with}
          self={here.dm_self}
        />
      ) : here.secret === true ? (
        <Lock size={16} aria-label={t('비공개 채널')} />
      ) : (
        <Hash size={16} aria-hidden />
      )}
      <span>{here.name}</span>
    </>
  )
  const tool = (label: string, icon: ReactNode, p: Panel) => (
    <IconButton
      size="sm"
      label={t(label)}
      icon={icon}
      aria-expanded={panel === p}
      onClick={() => show(p)}
    />
  )
  return (
    <header className="RoomHead">
      <div className="RoomHead-bar">
        <IconButton
          className="to-list"
          size="sm"
          label={t('채널 목록')}
          icon={<ChevronLeft aria-hidden />}
          onClick={to_list}
        />
        <h2 className="RoomHead-name">
          {open_edit === undefined ? (
            title
          ) : (
            // 슬랙처럼 이름을 누르면 채널 설정이 열린다 — ⋯ 메뉴 속에만 있으면 주제 하나 고치려고 메뉴를
            // 뒤져야 한다(3차 F197).
            <button
              type="button"
              className="RoomHead-open"
              aria-haspopup="dialog"
              aria-label={t('{name} 채널 세부정보', { name: here.name })}
              onClick={open_edit}
            >
              {title}
            </button>
          )}
        </h2>
        {manage?.row !== undefined && <StarButton here={here} hands={manage.row} />}
        {here.external !== undefined && here.external.length > 0 && (
          <Badge
            tone="accent"
            className="external"
            title={t('이 채널의 메시지는 연결된 워크스페이스에도 함께 표시돼요.')}
          >
            <ArrowLeftRight size={12} aria-hidden />{' '}
            {t('{orgs:와/과} 공유 중', { orgs: here.external.join(', ') })}
          </Badge>
        )}
        {here.topic != null && here.topic.length > 0 ? (
          open_edit === undefined ? (
            <p className="topic" title={here.topic}>
              {here.topic}
            </p>
          ) : (
            <Tooltip content={here.topic}>
              <button type="button" className="topic" onClick={open_edit}>
                {here.topic}
              </button>
            </Tooltip>
          )
        ) : (
          open_edit !== undefined &&
          manage?.manager === true && (
            <button
              type="button"
              className="topic add"
              // 주제를 적으러 누른 것이다 — 세부정보 창을 거쳐 [이름·주제 고치기] 까지 세 번이던 길을 한 번에, 초점은
              // 주제 칸에(3차 F261). 고칠 손이 없으면 예전처럼 세부정보.
              onClick={
                manage.edit_channel === undefined
                  ? open_edit
                  : () => {
                      show('edit')
                      focus_later('[data-field="topic"]')
                    }
              }
            >
              {t('주제 추가')}
            </button>
          )
        )}
        {manage !== undefined && (
          <div className="RoomHead-tools" role="toolbar" aria-label={t('채널 도구')}>
            {manage.huddle !== undefined && (
              <HuddleButton hands={manage.huddle} channel={here.id} />
            )}
            {here.dm !== true && (
              // 슬랙처럼 머리 오른쪽에 구성원 수 — 누르면 구성원 판(3차 F31).
              <Button
                size="sm"
                variant="ghost"
                className="RoomHead-count"
                icon={<UsersRound size={14} aria-hidden />}
                aria-label={
                  count === undefined ? t('채널 구성원') : t('구성원 {n}명', { n: count })
                }
                aria-haspopup="dialog"
                // 슬랙처럼 수를 누르면 세부정보의 구성원 탭(G21). 명단 판은 사이드바 너비를 늘 쓰지 않는다.
                onClick={() => set_details('members')}
              >
                {/* 수를 기다리는 동안에도 단추는 선다 — 없다가 서면 옆 도구가 밀린다(F322). */}
                {count ?? <span className="RoomHead-count-wait">0</span>}
              </Button>
            )}
            {manage.canvas !== undefined && tool(msg('캔버스'), <FileText aria-hidden />, 'canvas')}
            {tool(
              here.dm === true ? msg('사람 더하기') : msg('사람 추가'),
              <UserPlus aria-hidden />,
              'invite',
            )}
            <IconButton
              size="sm"
              label={here.dm === true ? t('대화 옵션') : t('채널 옵션')}
              icon={<Ellipsis aria-hidden />}
              aria-haspopup="menu"
              aria-expanded={more !== null}
              onClick={(e) => set_more(e.currentTarget)}
            />
          </div>
        )}
      </div>
      {manage !== undefined && more !== null && (
        <Suspense fallback={null}>
          <RoomMenuAt
            anchor={more}
            label={t('{name} 채널 메뉴', { name: here.name })}
            items={room_items(manage, show, t, here.dm === true)}
            last={
              manage.close_dm === undefined
                ? {
                    label: t('채널 나가기'),
                    // 비공개인지 알고 있으니 얼버무리지 않는다(3차 F139).
                    ask:
                      here.secret === true
                        ? {
                            title: t('비공개 채널 {name}에서 나갈까요?', { name: here.name }),
                            description: t('다시 들어오려면 안에 있는 사람이 초대해 줘야 해요.'),
                          }
                        : {
                            title: t('#{name} 채널에서 나갈까요?', { name: here.name }),
                            description: t('채널 둘러보기에서 언제든 다시 들어올 수 있어요.'),
                          },
                    act: manage.leave,
                  }
                : { label: t('DM 닫기'), act: async () => manage.close_dm?.() }
            }
            convert={
              here.dm === true && (here.dm_with?.length ?? 0) >= 2 ? manage.convert_dm : undefined
            }
            close={() => set_more(null)}
          />
        </Suspense>
      )}
      {manage !== undefined && (
        <RoomBookmarks
          channel={here.id}
          manage={manage}
          pins={
            manage.pins === undefined || pinned === undefined
              ? undefined
              : { count: pinned, open: panel === 'pins', show: () => show('pins') }
          }
        />
      )}
      {manage !== undefined && details !== null && (
        <Suspense fallback={null}>
          <ChannelDetails
            channel={here.id}
            name={here.name}
            topic={here.topic}
            secret={here.secret === true}
            people={people}
            first={details}
            can={can}
            go={show}
            {...(pick_person === undefined ? {} : { pick: pick_person })}
            leave={manage.leave}
            kick={manage.kick}
            close={() => set_details(null)}
          />
        </Suspense>
      )}
    </header>
  )
}

/** 타임라인에 넘길 것들. 있을 때만 넘긴다 — 없는 열쇠와 `undefined` 는 다르다. */
function list_of(props: WorkspaceProps & { on_who?(user: UserId): void }) {
  return {
    ...marks_of(props),
    ...(props.loading === undefined ? {} : { loading: props.loading }),
    ...(props.reached_start === undefined ? {} : { reached_start: props.reached_start }),
    ...(props.on_reach_top === undefined ? {} : { on_reach_top: props.on_reach_top }),
    ...(props.on_edit === undefined ? {} : { on_edit: props.on_edit }),
    ...(props.on_delete === undefined ? {} : { on_delete: props.on_delete }),
    ...(props.on_react === undefined ? {} : { on_react: props.on_react }),
    ...(props.on_thread === undefined ? {} : { on_thread: props.on_thread }),
    ...(props.tallies === undefined ? {} : { tallies: props.tallies }),
    ...(props.polls === undefined ? {} : { polls: props.polls }),
    ...(props.on_poll_vote === undefined ? {} : { on_poll_vote: props.on_poll_vote }),
    ...(props.on_poll_unvote === undefined ? {} : { on_poll_unvote: props.on_poll_unvote }),
    ...(props.on_poll_close === undefined ? {} : { on_poll_close: props.on_poll_close }),
    ...(props.reply_counts === undefined ? {} : { reply_counts: props.reply_counts }),
    ...(props.reply_last === undefined ? {} : { reply_last: props.reply_last }),
    ...(props.files === undefined ? {} : { files: props.files }),
    ...(props.cards === undefined ? {} : { cards: props.cards }),
    ...(props.on_who === undefined ? {} : { on_who: props.on_who }),
  }
}

/** 표시에 관한 것들만. `list_of` 가 한 함수로 다 지기엔 넘길 것이 많다. */
function marks_of(props: WorkspaceProps) {
  return {
    ...(props.on_pin === undefined ? {} : { on_pin: props.on_pin }),
    ...(props.on_save === undefined ? {} : { on_save: props.on_save }),
    ...(props.on_remind === undefined ? {} : { on_remind: props.on_remind }),
    ...(props.on_unread === undefined ? {} : { on_unread: props.on_unread }),
    ...(props.on_link === undefined ? {} : { on_link: props.on_link }),
    ...(props.on_forward === undefined ? {} : { on_forward: props.on_forward }),
    ...(props.on_hide_card === undefined ? {} : { on_hide_card: props.on_hide_card }),
    ...(props.on_action === undefined ? {} : { on_action: props.on_action }),
    ...(props.shortcuts === undefined ? {} : { shortcuts: props.shortcuts }),
    ...(props.on_shortcut === undefined ? {} : { on_shortcut: props.on_shortcut }),
    ...(props.emoji_names === undefined ? {} : { emoji_names: props.emoji_names }),
    ...(props.pinned === undefined ? {} : { pinned: props.pinned }),
    ...(props.saved === undefined ? {} : { saved: props.saved }),
  }
}

/** 채널 하나의 본문·쓰는 중·글쓰기. `exactOptionalPropertyTypes` 때문에 옵션은 있을 때만 넘긴다. */
function Conversation(
  props: WorkspaceProps & {
    here: ChannelPick
    show(p: Panel): void
    panel: Panel
    to_list(): void
    focus: Seq | null
    on_who?(user: UserId): void
  },
) {
  const { here, manage, show } = props
  const t = useT()
  // ↑(빈 입력칸) → 내 마지막 글 고치기. 번호를 올려 같은 줄을 또 불러도 다시 열린다.
  const [edit_ask, set_edit_ask] = useState<{ seq: Seq; n: number } | null>(null)
  // 1:1 대화방의 상대. 여럿의 방·모르는 방은 없다.
  const one = here.dm === true && here.dm_with?.length === 1 ? here.dm_with[0] : undefined
  const note = is_note(here)
  const edit_last =
    props.on_edit === undefined
      ? undefined
      : () => {
          const last = props.lines.findLast((l) => l.author === props.me && l.deleted_at === null)
          if (last === undefined) return false
          set_edit_ask((a) => ({ seq: last.seq, n: (a?.n ?? 0) + 1 }))
          return true
        }
  return (
    <>
      <RoomHead
        here={here}
        to_list={props.to_list}
        show={show}
        panel={props.panel}
        {...(manage === undefined ? {} : { manage })}
        count={humans_in(props.people)}
        people={props.people}
        pick_person={props.on_who}
        pinned={props.pinned?.size}
      />
      <MessageList
        lines={props.lines}
        read_upto={props.read_upto}
        name_of={props.name_of}
        bot_of={props.bot_of}
        group_of={props.group_of}
        emoji_of={props.emoji_of}
        me={props.me}
        {...list_of(props)}
        focus={props.focus}
        words={props.words}
        edit_ask={edit_ask}
        // 채널의 처음 — 이름과 주제를 한 번 더 말한다(슬랙의 채널 인트로, 3차 F32).
        intro={
          note
            ? {
                // 나 혼자의 방에 "나와의 대화 님과 나눈 대화" 라고 적혀 있었다(3차 F305). 슬랙처럼 쓸모를 말한다.
                title: t('나만 보는 공간이에요'),
                detail: t('메모·할 일·링크를 적어 두세요. 리마인더도 여기로 와요.'),
                face: (
                  <Avatar
                    size="xl"
                    name={here.dm_self?.name || here.name}
                    user={props.me}
                    decorative
                  />
                ),
              }
            : here.dm === true
              ? {
                  title: t('{name} 님과 나눈 대화의 시작이에요', { name: here.name }),
                  // 1:1 방의 처음에는 그 사람의 얼굴 — 누구와의 방인지 이름보다 먼저 읽힌다(3차 F259).
                  ...(one === undefined
                    ? {}
                    : { face: <Avatar size="xl" name={here.name} user={one} decorative /> }),
                }
              : {
                  title:
                    here.secret === true
                      ? t('비공개 채널 {name}의 시작이에요', { name: here.name })
                      : t('#{name} 채널의 시작이에요', { name: here.name }),
                  detail:
                    here.topic != null && here.topic !== ''
                      ? here.topic
                      : t('이 채널의 주제에 맞는 이야기를 나눠 보세요.'),
                }
        }
        // 막 만든 채널은 비어 있다 — 다음 할 일은 사람을 부르는 것이다(3차 J3). 대화방은 명단이 곧 방이라 없다.
        empty_action={
          here.dm === true ? (
            one !== undefined && props.on_who !== undefined ? (
              <Button onClick={() => props.on_who?.(one)}>{t('프로필 보기')}</Button>
            ) : undefined
          ) : manage === undefined ? undefined : (
            <Button icon={<UserPlus aria-hidden />} onClick={() => show('invite')}>
              {t('사람 추가')}
            </Button>
          )
        }
      />
      {props.whispers !== undefined && props.dismiss_whisper !== undefined && (
        <Whispers
          items={props.whispers}
          name_of={props.name_of}
          me={props.me}
          group_of={props.group_of}
          emoji_of={props.emoji_of}
          dismiss={props.dismiss_whisper}
        />
      )}
      <TypingLine names={props.typing_names ?? []} />
      <Writing {...props} here={here} {...(edit_last === undefined ? {} : { edit_last })} />
    </>
  )
}

function Writing(props: WorkspaceProps & { here: ChannelPick; edit_last?(): boolean }) {
  const t = useT()
  const here = props.here.id
  const draft = props.draft_of?.(here) ?? ''
  // 사람과 그룹은 한 메뉴에서 같이 뜬다 — 부를 때 사람은 둘을 구분하지 않는다.
  const callable: Candidate[] | undefined =
    props.people === undefined && props.groups === undefined
      ? undefined
      : [...(props.people ?? []), ...(props.groups ?? [])]
  if (props.here.locked === true) {
    return (
      <p className="Locked" role="note">
        {t(
          '공지 채널이에요. 관리자만 메시지를 보낼 수 있고, 스레드에는 누구나 답글을 달 수 있어요.',
        )}
      </p>
    )
  }
  return (
    <Composer
      // 채널이 바뀌면 입력칸을 **새로** 만든다. 그래야 그 채널의 초안이 처음 값으로 들어간다.
      key={here}
      // 채널을 열면 곧 쓴다 — 초점이 사이드바에 남으면 한 번 더 눌러야 친다. 좁은 화면은 빼는데,
      // 거기서는 초점이 곧 자판이라 채널을 열자마자 글이 절반 가려진다.
      autoFocus={!narrow()}
      // 채널은 `#` 을 붙여 부른다 — 머리글처럼 채널인 줄 안내만 보고도 안다(슬랙 "#일반에 메시지 보내기", 3차 F343).
      channel_name={`#${props.here.name}`}
      // 사람에게는 '에' 가 아니라 '에게' 다 — "박도윤에 메시지 보내기" 는 틀린 말이다(3차 J10).
      {...(is_note(props.here)
        ? { placeholder: t('나에게 메모 남기기') }
        : props.here.dm === true
          ? { placeholder: t('{name} 님에게 메시지 보내기', { name: props.here.name }) }
          : {})}
      draft={draft}
      {...(props.on_draft === undefined
        ? {}
        : { on_draft: (text: string) => props.on_draft?.(here, text) })}
      send={props.send}
      {...(props.attach === undefined ? {} : { attach: props.attach })}
      record={props.record}
      transcribe={props.transcribe}
      {...(props.typing === undefined ? {} : { typing: props.typing })}
      {...(callable === undefined ? {} : { people: callable })}
      {...(props.commands === undefined ? {} : { commands: props.commands })}
      {...(props.run === undefined ? {} : { run: props.run })}
      {...(props.load_commands === undefined ? {} : { load_commands: props.load_commands })}
      {...(props.schedule === undefined ? {} : { schedule: props.schedule })}
      {...(props.poll === undefined ? {} : { poll: props.poll })}
      {...(props.edit_last === undefined ? {} : { edit_last: props.edit_last })}
      emoji_of={props.emoji_of}
      emoji_names={props.emoji_names}
      {...(props.globals === undefined || props.on_global === undefined
        ? {}
        : {
            shortcuts: props.globals,
            on_shortcut: (key: string) => props.on_global?.(here, key) ?? Promise.resolve(),
          })}
    />
  )
}

/** 채널 머리의 북마크 줄. 손이 없으면 그리지 않는다. */
function RoomBookmarks({
  channel,
  manage,
  pins,
}: {
  channel: ChannelId
  manage: Manage
  pins?: { count: number; open: boolean; show(): void } | undefined
}) {
  const { bookmarks, bookmark_add, bookmark_remove, bookmark_edit } = manage
  const load = () => (bookmarks === undefined ? Promise.resolve([]) : bookmarks(channel))
  if (bookmarks === undefined) return null
  return (
    // 줄 청크를 받는 동안에도 줄 높이를 잡는다 — 늦게 서면 글줄 전체가 한 줄만큼 밀려 내려간다(3차 F325).
    <Suspense fallback={<div className="BookmarkShelf" data-wait aria-hidden />}>
      <BookmarkBar
        key={`${channel}:${manage.bookmarks_rev ?? 0}`}
        load={load}
        pins={pins}
        {...(bookmark_add === undefined
          ? {}
          : { add: (title: string, url: string) => bookmark_add(channel, title, url) })}
        {...(bookmark_remove === undefined
          ? {}
          : { remove: (id: number) => bookmark_remove(channel, id) })}
        {...(bookmark_edit && { edit: bookmark_edit.bind(null, channel) })}
      />
    </Suspense>
  )
}
